import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, from, of } from 'rxjs';
import { switchMap, map, catchError } from 'rxjs/operators';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BASE_PATH } from '@app/api/stock-service/variables';

import { InboundNoticeResultVO } from '@app/api/stock-service/model/inboundNoticeResultVO';
import { PurchaseOrderService } from '@app/feature/center-business/procurement/purchase-order/services/purchase-order.service';
import { PurchaseOrderItem } from '@app/feature/center-business/procurement/purchase-order/purchase-order.config';
import { PurchaseOrderDetailItem } from '@app/feature/center-business/procurement/purchase-order/purchase-order-add/purchase-order-add.config';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { WarehouseWarrantInfoVO } from '@app/api/model/warehouseWarrantInfoVO';
import { WarehouseWarrantDetailItem } from '../../models/warehouse-warrant-detail-item';
import { InboundService } from '../../inbound.service';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  getInboundStatusSeverity,
  getInboundStatusStyle,
} from '../../inbound.util';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';

type InboundNoticeWrapperData = Partial<WarehouseWarrantInfoVO> & {
  innerOrderNo?: string;
  warehouseName?: string;
  remarks?: string;
  realNoticeCode?: string;
  expectedArrivalDate?: string;
  trackingNumber?: string;
  carrierName?: string;
};

type InboundStatus = number | string | undefined;

@Component({
  selector: 'app-inbound-notice-view',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    InputTextModule,
    TextareaModule,
    ProgressSpinnerModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './inbound-notice-view.component.html',
  styleUrl: './inbound-notice-view.component.css',
})
export class InboundNoticeViewComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly purchaseOrderService = inject(PurchaseOrderService);
  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly translateService = inject(TranslateService);
  private readonly http = inject(HttpClient);
  private readonly basePath = inject(BASE_PATH);
  private readonly destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  saving = signal(false);
  editMode = signal(false);

  // Record details
  record = signal<InboundNoticeResultVO | null>(null);
  recordId = signal<number | null>(null);
  recordCode = signal<string | null>(null);

  // Purchase Order data
  purchaseOrder = signal<PurchaseOrderItem | null>(null);
  purchaseOrderItems = signal<PurchaseOrderDetailItem[]>([]);
  inboundDataForWrapper = signal<InboundNoticeWrapperData | null>(null);
  inboundDetailItemsForWrapper = signal<WarehouseWarrantDetailItem[]>([]);

  // Warehouse options
  // Form
  noticeForm!: FormGroup;

  // Table columns for Item Details
  itemTableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_code',
      prop: 'skuCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'inbound.fields.spu_code',
      prop: 'spuCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.spu_name',
      prop: 'spuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'inbound.fields.quantity',
      prop: 'plannedQty',
      type: 'text',
      width: 120,
      align: 'right',
    },
  ];

  // Table columns for PO items (view-only)
  poItemsTableColumns: TableColumn[] = [
    {
      label: 'purchase_order.add.table.sku_code',
      prop: 'skuCode',
      type: 'text',
      width: 120,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.sku_name',
      prop: 'skuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.spu_code',
      prop: 'spuCode',
      type: 'text',
      width: 120,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.spu_name',
      prop: 'spuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.quantity',
      prop: 'quantity',
      type: 'text',
      width: 100,
      align: 'right',
    },
    {
      label: 'purchase_order.add.table.warehouse',
      prop: 'warehouseCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'purchase_order.add.table.unit_price',
      prop: 'unitPrice',
      type: 'text',
      width: 120,
      align: 'right',
    },
    {
      label: 'purchase_order.add.table.tax',
      prop: 'tax',
      type: 'text',
      width: 100,
      align: 'right',
    },
    {
      label: 'purchase_order.add.table.total_value',
      prop: 'totalValue',
      type: 'currency',
      width: 120,
      align: 'right',
    },
  ];

  ngOnInit(): void {
    this.initializeModeFromRoute();
    this.initializeForm();
    if (this.editMode()) {
      this.enableEditMode();
    }

    this.loadRecordDetails();
  }

  private initializeModeFromRoute(): void {
    const path = this.route.snapshot.routeConfig?.path || '';
    const isEdit = path.includes('edit');
    this.editMode.set(isEdit);
    if (isEdit && this.noticeForm) {
      this.enableEditMode();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.noticeForm = this.fb.group({
      noticeCode: ['', Validators.required],
      warehouseCode: [''],
      warehouseName: [''],
      deliveryDate: [null],
      status: [''],
      remarks: [''],
      creationDate: [''],
    });
  }

  private resolveWarehouseDisplayName(
    code: string,
    fallback?: string,
  ): string {
    const cached = this.warehouseOptionsService.getWarehouseByCode(code);
    if (cached?.name) {
      return cached.name;
    }
    return fallback || code || '';
  }

  private prefetchWarehouseCode(code: string) {
    if (!code || this.warehouseOptionsService.getWarehouseByCode(code)) {
      return of(undefined);
    }
    return this.warehouseOptionsService
      .getOptionsPage({ page: 1, size: 1, searchText: code })
      .pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  private loadRecordDetails(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const code = this.route.snapshot.queryParams['code'];

    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.onBack();
      return;
    }

    const noticeId = Number.parseInt(id);
    if (Number.isNaN(noticeId)) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('common.messages.invalid_id'),
      });
      this.onBack();
      return;
    }

    this.recordId.set(noticeId);
    if (code) {
      this.recordCode.set(code);
    }

    this.loading.set(true);

    // Load notice data from API
    const url = `${this.basePath}/v1/inbound-notice/${noticeId}`;
    this.http
      .get<InboundNoticeResultVO>(url, {
        headers: new HttpHeaders({ Accept: 'application/json' }),
        observe: 'body',
        responseType: 'json',
      })
      .pipe(
        switchMap((response: any) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (notice: InboundNoticeResultVO) => {
          if (notice) {
            this.record.set(notice);
            if (notice.code) {
              this.recordCode.set(notice.code);
            }
            // Map details from notice.details to purchaseOrderItems
            if (notice.details && notice.details.length > 0) {
              const items: PurchaseOrderDetailItem[] = notice.details.map(
                (detail) => ({
                  id: detail.id?.toString() || '',
                  skuCode: detail.skuCode || '',
                  skuName: detail.skuName || '',
                  spuCode: detail.spuCode || '',
                  spuName: detail.spuName || '',
                  quantity: detail.expectedQty || 0,
                  warehouseCode: notice.warehouseCode || '',
                  unit: '',
                  unitPrice: detail.unitPrice || 0,
                  tax: 0,
                  totalValue:
                    (detail.expectedQty || 0) * (detail.unitPrice || 0),
                }),
              );
              this.purchaseOrderItems.set(items);
            }

            // Populate form with mapped warehouse name
            this.populateForm(notice);
            // Update wrapper data with mapped warehouse name
            this.updateWrapperData(notice);
            this.loading.set(false);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.messages.error'),
              detail: this.translateService.instant(
                'inbound.notice.messages.not_found',
              ),
            });
            this.loading.set(false);
            this.onBack();
          }
        },
        error: (error: any) => {
          console.error('Error loading notice:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'inbound.notice.messages.failed_to_load',
            ),
          });
          this.onBack();
        },
      });
  }

  private loadPurchaseOrder(orderCode: string): void {
    // Search for PO by order code
    this.purchaseOrderService
      .getPurchaseOrders({ series: orderCode, page: 0, size: 1 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response.data && response.data.length > 0) {
            const po = response.data[0];
            this.purchaseOrder.set(po);

            // Load PO detail to get items
            this.purchaseOrderService
              .getPurchaseOrderById(po.id.toString())
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: (poDetail) => {
                  if (poDetail?.items) {
                    const items: PurchaseOrderDetailItem[] = poDetail.items.map(
                      (item: any) => ({
                        id: item.id?.toString() || '',
                        skuCode: item.skuCode || '',
                        skuName: item.skuName || '',
                        spuCode: item.spuCode || '',
                        spuName: item.spuName || '',
                        quantity: item.quantity || 0,
                        warehouseCode: item.warehouseCode || '',
                        unit: item.unit || '',
                        unitPrice: item.unitPrice || 0,
                        tax: item.tax || 0,
                        totalValue: item.totalValue || 0,
                      }),
                    );
                    this.purchaseOrderItems.set(items);
                  }
                },
                error: () => {
                  // Silently handle error - PO detail is optional
                },
              });
          }
        },
        error: () => {
          // Silently handle error - PO loading is optional
        },
      });
  }

  private populateForm(notice: InboundNoticeResultVO): void {
    if (!this.noticeForm) {
      return;
    }

    const warehouseCode = notice.warehouseCode || '';
    this.prefetchWarehouseCode(warehouseCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const warehouseName = this.resolveWarehouseDisplayName(
          warehouseCode,
          notice.warehouseName,
        );

        let deliveryDate: Date | null = null;
        if (notice.expectedArrivalDate) {
          deliveryDate = new Date(notice.expectedArrivalDate);
        }

        this.noticeForm.patchValue({
          noticeCode: notice.code || '',
          warehouseCode: warehouseCode,
          warehouseName: warehouseName,
          deliveryDate: deliveryDate,
          status: notice.status?.toString() || '',
          remarks: notice.remarks || '',
          creationDate: notice.creationDate || '',
        });

        if (!this.editMode()) {
          this.noticeForm.disable();
        }
      });
  }

  onBack(): void {
    this.router.navigate(['/stock/inbound'], {
      queryParams: { tab: 'inbound-notice' },
    });
  }

  onEdit(): void {
    this.enableEditMode();
  }

  onCancel(): void {
    this.editMode.set(false);
    this.onBack();
  }

  onSave(): void {
    // Get latest values from noticeForm (which should be synced from editForm)
    if (this.noticeForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Vui lòng điền đầy đủ thông tin bắt buộc',
      });
      return;
    }

    this.saving.set(true);

    // TODO: Call API to update notice
    // For now, just show success message
    setTimeout(() => {
      this.saving.set(false);
      this.editMode.set(false);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Cập nhật thông báo nhập kho thành công',
      });
      this.noticeForm.disable();
      this.onBack();
    }, 1000);
  }

  private updateWrapperData(notice: InboundNoticeResultVO): void {
    const warehouseName = this.resolveWarehouseDisplayName(
      notice.warehouseCode || '',
      notice.warehouseName,
    );

    this.inboundDataForWrapper.set({
      id: notice.id ?? undefined,
      code: notice.code,
      innerOrderNo: notice.sourceCode, // sourceCode is the PO code
      warehouseName: warehouseName,
      expectedArrivalDate: notice.expectedArrivalDate,
      status: notice.status?.toString(),
      remarks: notice.remarks || '',
      creationDate: notice.creationDate,
      realNoticeCode: notice.realNoticeCode || '',
      trackingNumber: notice.trackingNumber || '',
      carrierName: notice.carrierName || '',
    });

    const mappedDetails: WarehouseWarrantDetailItem[] =
      this.purchaseOrderItems().map((item) => ({
        id: item.id ? Number(item.id) : undefined,
        skuCode: item.skuCode,
        skuName: item.skuName,
        spuCode: item.spuCode,
        spuName: item.spuName,
        plannedQty: item.quantity,
        alreadyQty: item.quantity,
        policy: {},
      }));

    this.inboundDetailItemsForWrapper.set(mappedDetails);
    this.updateFormattedItemDetails();
  }

  private formatDeliveryDate(date: Date | null): string {
    if (!date) return '';
    try {
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }

  private patchNoticeForm(data: any): void {
    if (!data || !this.noticeForm) return;
    if (data.code !== undefined) {
      this.noticeForm.patchValue({ noticeCode: data.code });
    }
    if (data.deliveryDate !== undefined) {
      this.noticeForm.patchValue({ deliveryDate: data.deliveryDate });
    }
    if (data.remarks !== undefined) {
      this.noticeForm.patchValue({ remarks: data.remarks });
    }
  }

  onFormAction(event: { action: string; data?: any }): void {
    switch (event?.action) {
      case 'fieldChange':
        this.patchNoticeForm(event.data);
        break;
      case 'go_back':
        this.onBack();
        break;
      case 'cancel':
        this.onCancel();
        break;
      case 'save':
        this.patchNoticeForm(event.data);
        this.onSave();
        break;
    }
  }

  // Placeholder to align with wrapper outputs
  onTableAction(event?: any): void {
    // no-op for view mode
  }

  onStatusChange(event?: any): void {
    // no-op for view mode
  }

  private enableEditMode(): void {
    this.editMode.set(true);
    this.noticeForm.enable();
    this.noticeForm.get('warehouseCode')?.disable();
    this.noticeForm.get('warehouseName')?.disable();
    this.noticeForm.get('status')?.disable();
    this.noticeForm.get('creationDate')?.disable();
  }

  onCreateWarrant(): void {
    // Navigate to create inbound warrant page
    this.router.navigate(['/stock/inbound/add'], {
      queryParams: {
        tab: 'inbound-warrant',
        noticeId: this.recordId(),
      },
    });
  }

  getStatusLabel(status: InboundStatus): string {
    if (!status) return '';

    // Handle string status (PENDING, RECEIVING, RECEIVED, CANCELLED)
    if (typeof status === 'string') {
      const statusMap: { [key: string]: string } = {
        PENDING: this.translateService.instant('inbound.status.pending'),
        RECEIVING: this.translateService.instant('inbound.status.receiving'),
        RECEIVED: this.translateService.instant('inbound.status.received'),
        CANCELLED: this.translateService.instant('inbound.status.cancelled'),
        CREATED: this.translateService.instant('inbound.status.created'),
        CONFIRMED: this.translateService.instant('inbound.status.confirmed'),
        IN_TRANSIT: this.translateService.instant('inbound.status.in_transit'),
        ARRIVED: this.translateService.instant('inbound.status.arrived'),
        PARTIALLY_RECEIVED: this.translateService.instant(
          'inbound.status.partially_received',
        ),
        CLOSED: this.translateService.instant('inbound.status.closed'),
      };
      if (statusMap[status]) {
        return statusMap[status];
      }
    }

    // Handle numeric status
    const statusNum =
      typeof status === 'string' ? Number.parseInt(status) : status;
    const statusMap: { [key: number]: string } = {
      1: this.translateService.instant('inbound.status.created'),
      2: this.translateService.instant('inbound.status.confirmed'),
      3: this.translateService.instant('inbound.status.in_transit'),
      4: this.translateService.instant('inbound.status.arrived'),
      5: this.translateService.instant('inbound.status.receiving'),
      6: this.translateService.instant('inbound.status.partially_received'),
      7: this.translateService.instant('inbound.status.received'),
      8: this.translateService.instant('inbound.status.closed'),
      9: this.translateService.instant('inbound.status.cancelled'),
    };
    return statusMap[statusNum] || status.toString();
  }

  getStatusSeverity(
    status: InboundStatus,
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return getInboundStatusSeverity(status);
  }

  getStatusStyle(status: InboundStatus): any {
    return getInboundStatusStyle(status);
  }

  formatDate(dateString: string | undefined): string {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;

      const time = date.toLocaleTimeString('vi-VN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const datePart = date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

      return `${time} ${datePart}`; // e.g., 16:49 09/12/2025
    } catch {
      return dateString;
    }
  }

  getMinDate(): Date {
    return new Date();
  }

  // Get field value from inboundDataForWrapper
  getFieldValue(fieldName: keyof InboundNoticeWrapperData): string | undefined {
    const data = this.inboundDataForWrapper();
    if (!data) return undefined;
    return data[fieldName] as string | undefined;
  }

  // Format date only (without time)
  formatDateOnly(dateString: string | undefined): string {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  }

  // Format item details with formatted numbers (plannedQty is formatted as string)
  formattedItemDetails = signal<any[]>([]);

  private updateFormattedItemDetails(): void {
    const items = this.inboundDetailItemsForWrapper().map((item) => ({
      ...item,
      plannedQty: this.formatNumber(item.plannedQty),
    }));
    this.formattedItemDetails.set(items);
  }

  private formatNumber(value: any): string {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'undefined' ||
      value === 'null'
    ) {
      return ' ';
    }
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
      return ' ';
    }
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }
}
